import { matchRoutes, type RouteObject } from "react-router-dom";
import type { BeforeSend } from "@vercel/analytics/react";

/**
 * Reduce a URL to its route pattern before Vercel Analytics sees it.
 *
 * `/org/<uuid>/accounting/receivables/<id>?tab=x#y` →
 * `/org/[orgId]/accounting/receivables/[invoiceId]`
 *
 * Two jobs: group page views by page instead of by org/record, and keep
 * record ids, file names, OAuth codes (`/auth/callback`, `/bank-callback`) and
 * invitation tokens (`?token=`) out of a third-party service. The query
 * string and hash are always dropped. A path no route matches is reported as
 * `/[unmatched]` rather than echoed.
 */
export function toRoutePattern(routes: RouteObject[], pathname: string): string {
  const matches = matchRoutes(routes, pathname);
  if (!matches) return "/[unmatched]";

  const segments: string[] = [];
  for (const { route } of matches) {
    if (!route.path) continue; // index / layout routes add no segment
    if (route.path.startsWith("/")) segments.length = 0;
    for (const segment of route.path.split("/")) {
      if (!segment) continue;
      if (segment === "*") segments.push("[...]");
      else if (segment.startsWith(":")) segments.push(`[${segment.slice(1).replace(/\?$/, "")}]`);
      else segments.push(segment);
    }
  }
  return `/${segments.join("/")}`;
}

export function createAnalyticsBeforeSend(routes: RouteObject[]): BeforeSend {
  return (event) => {
    const url = new URL(event.url);
    return { ...event, url: `${url.origin}${toRoutePattern(routes, url.pathname)}` };
  };
}

// Where a URL segment's own name misreads in the UI's vocabulary.
const PAGE_SEGMENT_NAMES: Record<string, string> = {
  receivables: "Invoices",
  payables: "Bills",
  assets: "Things",
  s: "Survey",
};

function humanizeSegment(segment: string): string {
  if (segment === "[...]") return "Not found";
  if (segment.startsWith("[")) return "Detail";
  return (
    PAGE_SEGMENT_NAMES[segment] ??
    segment
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  );
}

/**
 * A readable page name for analytics, derived from a route pattern (never a
 * raw path, so it cannot carry ids): `/org/[orgId]/accounting/receivables/[invoiceId]`
 * → `Accounting / Invoices / Detail`. Fixed English, independent of the
 * user's locale, so a page keeps one name in reports.
 */
export function pageNameFor(pattern: string): string {
  if (pattern === "/[unmatched]") return "Unmatched";
  const segments = pattern.replace(/^\/org\/\[orgId\]/, "").split("/").filter(Boolean);
  return segments.length ? segments.map(humanizeSegment).join(" / ") : "Home";
}
